import Keycloak from "keycloak-js";

export class KeycloakService {
  private keycloak: Keycloak;
  private refreshTimeout: NodeJS.Timeout | null = null;
  private setToken: (token: string | null) => void;

  constructor(setToken: (token: string | null) => void) {
    const url = process.env.REACT_APP_KEYCLOAK_BASE_URL;
    const realm = process.env.REACT_APP_KEYCLOAK_REALM;
    const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;

    if (!url || !realm || !clientId) {
      throw new Error("Missing Keycloak configuration");
    }

    this.keycloak = new Keycloak({ url, realm, clientId });
    this.setToken = setToken;
  }

  async init(): Promise<Keycloak> {
    try {
      const authenticated = await this.keycloak.init({
        onLoad: "login-required",
        redirectUri: window.location.origin,
        enableLogging: process.env.NODE_ENV !== "production",
        checkLoginIframe: false,
      });

      if (authenticated) {
        console.log("Keycloak Authenticated");
        this.setToken(this.keycloak.token!);
        this.scheduleTokenRefresh();
      }

      return this.keycloak;
    } catch (error) {
      console.error("Keycloak initialization failed:", error);
      throw error;
    }
  }

  private scheduleTokenRefresh() {
    if (!this.keycloak.tokenParsed || !this.keycloak.tokenParsed.exp) {
      console.error("Keycloak token is missing.");
      return;
    }

    const expiresIn = this.keycloak.tokenParsed.exp * 1000 - Date.now();

    if (expiresIn <= 0) {
      console.warn("Token already expired, triggering immediate refresh.");
      this.refreshToken();
      return;
    }

    const refreshInMs = expiresIn - 30000;
    console.log(`Scheduling token refresh in ${refreshInMs / 1000} seconds`);

    this.clearTokenRefresh();

    this.refreshTimeout = setTimeout(() => this.refreshToken(), refreshInMs);
  }

  private async refreshToken() {
    try {
      const refreshed = await this.keycloak.updateToken(60);
      if (refreshed) {
        console.log("Token refreshed successfully.");
        this.setToken(this.keycloak.token!);
        this.scheduleTokenRefresh();
      }
    } catch (error) {
      console.error("Failed to refresh Keycloak token:", error);
    }
  }

  clearTokenRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }

  getKeycloakInstance(): Keycloak {
    return this.keycloak;
  }

  getCityId(): string {
    return this.keycloak.tokenParsed?.codeVille;
  }
  logout() {
    return this.keycloak.logout();
  }
  loggedIn() {
    return this.keycloak.authenticated;
  }
  login() {
    return this.keycloak.login();
  }
  createAccountUrl() {
    return this.keycloak.createAccountUrl();
  }
  getUserCredentials() {
    return {
      keepSessionOpen: "1",
      matricule: this.keycloak.tokenParsed?.matricule,
      pinCode: this.keycloak.tokenParsed?.terminalId,
    };
  }
  hasRoles(role: string) {
    return this.keycloak.tokenParsed?.roles.includes(role);
  }
}
